import React, { useEffect, useState } from 'react';
import { timelogAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiCheck, FiX, FiClock, FiCheckSquare, FiAlertCircle, FiFilter } from 'react-icons/fi';

export default function TimeApproval() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter]   = useState('pending'); // pending | approved | all

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const r = filter === 'pending'
        ? await timelogAPI.getPending()
        : await timelogAPI.getAll();
      let data = r.data.data || [];
      if (filter === 'approved') data = data.filter(l => l.isApproved);
      setLogs(data);
    } catch { toast.error('Failed to load timelogs'); }
    setLoading(false);
  };

  const approve = async (id, val) => {
    try {
      await timelogAPI.approve(id, { approved: val });
      toast.success(val ? 'Approved' : 'Rejected');
      load();
    } catch { toast.error('Failed'); }
  };

  const bulkApprove = async (val) => {
    if (!selected.size) return toast.error('Select timelogs first');
    try {
      await timelogAPI.bulkApprove({ ids: [...selected], approved: val });
      toast.success(`${selected.size} logs ${val ? 'approved' : 'rejected'}`);
      setSelected(new Set());
      load();
    } catch { toast.error('Failed'); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === logs.length) setSelected(new Set());
    else setSelected(new Set(logs.map(l => l._id)));
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDur = (mins) => mins ? `${Math.floor(mins/60)}h ${mins%60}m` : '—';

  const totalHours = logs.reduce((s, l) => s + (l.duration || 0), 0);
  const pendingCount = logs.filter(l => !l.isApproved).length;

  const S = {
    page: { maxWidth: 1200, margin: '0 auto', fontFamily: "'Sora',sans-serif" },
    card: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px #0000000a', border: '1px solid #f1f5f9' },
    btn: (v, sm) => ({
      padding: sm ? '6px 14px' : '9px 18px', borderRadius: 10, border: 'none', fontWeight: 600,
      fontSize: sm ? 12 : 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
      ...(v === 'approve' ? { background: '#d1fae5', color: '#065f46' }
        : v === 'reject'  ? { background: '#fee2e2', color: '#991b1b' }
        : v === 'primary' ? { background: 'var(--pm-gradient)', color: '#fff' }
        : { background: '#f8fafc', color: '#64748b', border: '1.5px solid #e2e8f0' })
    }),
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#0f172a' }}>⏱ Time Approval</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Review and approve submitted time logs</p>
        </div>
        {selected.size > 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btn('approve')} onClick={() => bulkApprove(true)}><FiCheck /> Approve {selected.size}</button>
            <button style={S.btn('reject')}  onClick={() => bulkApprove(false)}><FiX /> Reject {selected.size}</button>
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { icon: <FiAlertCircle />, label: 'Pending', value: pendingCount, color: '#f59e0b' },
          { icon: <FiCheck />,       label: 'Total Logs', value: logs.length, color: 'var(--pm-primary)' },
          { icon: <FiClock />,       label: 'Total Hours', value: fmtDur(totalHours), color: '#22c55e' },
          { icon: <FiCheckSquare />, label: 'Approved', value: logs.filter(l => l.isApproved).length, color: '#0ea5e9' },
        ].map(k => (
          <div key={k.label} style={{ ...S.card, borderLeft: `4px solid ${k.color}`, padding: '16px 20px' }}>
            <div style={{ color: k.color, fontSize: 20, marginBottom: 8 }}>{k.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>{k.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {[['pending', 'Pending'], ['approved', 'Approved'], ['all', 'All']].map(([v, l]) => (
          <button key={v} onClick={() => { setFilter(v); setSelected(new Set()); }}
            style={{ padding: '7px 18px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer',
              background: filter === v ? '#fff' : 'transparent', color: filter === v ? 'var(--pm-primary)' : '#64748b',
              boxShadow: filter === v ? '0 1px 4px #0000001a' : 'none', transition: 'all .2s' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left' }}>
                <input type="checkbox" checked={selected.size === logs.length && logs.length > 0}
                  onChange={toggleAll} style={{ cursor: 'pointer', width: 15, height: 15 }} />
              </th>
              {['Member', 'Project', 'Task', 'Date', 'Duration', 'Billable', 'Notes', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748b', borderBottom: '2px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={l._id} style={{ borderBottom: '1px solid #f8fafc', background: selected.has(l._id) ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <td style={{ padding: '12px 16px' }}>
                  <input type="checkbox" checked={selected.has(l._id)} onChange={() => toggleSelect(l._id)} style={{ cursor: 'pointer', width: 15, height: 15 }} />
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--pm-gradient)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                      {l.user?.name?.charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>{l.user?.name || '—'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{l.user?.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>{l.project?.name || '—'}</td>
                <td style={{ padding: '12px 16px', color: '#64748b' }}>{l.task?.title || '—'}</td>
                <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                  <div>{fmt(l.startTime)}</div>
                  {l.endTime && <div style={{ fontSize: 11, color: '#94a3b8' }}>to {fmt(l.endTime)}</div>}
                </td>
                <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--pm-primary)', whiteSpace: 'nowrap' }}>{fmtDur(l.duration)}</td>
                <td style={{ padding: '12px 16px' }}>
                  {l.isBillable
                    ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>₹{(l.billableAmount || 0).toLocaleString('en-IN')}</span>
                    : <span style={{ color: '#94a3b8', fontSize: 12 }}>Non-billable</span>}
                </td>
                <td style={{ padding: '12px 16px', color: '#475569', maxWidth: 160 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.notes || l.description || '—'}</div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {l.isApproved
                    ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>✓ Approved</span>
                    : <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>Pending</span>}
                </td>
                <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                  {!l.isApproved && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={S.btn('approve', true)} onClick={() => approve(l._id, true)}><FiCheck size={11} /> Approve</button>
                      <button style={S.btn('reject', true)}  onClick={() => approve(l._id, false)}><FiX size={11} /> Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
            <FiCheckSquare size={32} style={{ marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
            <div style={{ fontWeight: 600 }}>{filter === 'pending' ? 'No pending logs — all caught up!' : 'No logs found'}</div>
          </div>
        )}
      </div>
    </div>
  );
}
